import { canvasContext, makeCanvas, toBlob } from './canvas'
import type { DecodedImage } from './exif'
import type { LayoutConfig } from './types'

/**
 * Layout compositing, ported from PrintWithLayout.LoadPreviewImages().
 *
 * Fidelity notes, all deliberate:
 *
 *  - The output canvas is the BACKGROUND'S NATIVE PIXEL SIZE. Nothing is scaled
 *    to fit a page; print geometry happens later, in the printer.
 *  - Photos pair with slots BY INDEX. Photo i goes to slots[i], full stop.
 *    Extra photos are dropped (the original threw into an empty catch), and
 *    extra slots stay as bare background.
 *  - A photo whose orientation does not equal the slot's is SKIPPED, leaving
 *    the slot empty. "Square" therefore matches nothing. This surprises people,
 *    so `composite` reports it instead of hiding it.
 *  - Drawing uses the 9-argument drawImage, which stretches to fill without
 *    preserving aspect — the same semantics as WPF's DrawImage(src, Rect).
 *    For 2:3 slots that is a raw stretch, exactly as today.
 *  - The QR is drawn LAST, after every photo.
 */

export interface CompositeInput {
  layout: LayoutConfig
  /** Background at native resolution. */
  background: ImageBitmap
  /** Selected photos, already decoded and EXIF-rotated, in selection order. */
  images: DecodedImage[]
  /**
   * Drawn over the photos at the background's native size, or null.
   *
   * Stretched to the whole canvas rather than placed: it is a second full-sheet
   * layer, so it is authored against the same pixel grid as the background and
   * needs no geometry of its own.
   */
  overlay?: ImageBitmap | null
  /** QR bitmap, or null when the layout has no QR box. */
  qr: ImageBitmap | null
}

export interface CompositeResult {
  /** The sheet that gets printed: photos + QR. */
  print: Blob
  /**
   * The copy filed into the customer's session folder.
   *
   * The WPF build renders this from the same DrawingVisual AFTER the QR is
   * drawn, so `stored_<ts>.png` and `<ts>.png` come out byte-identical and both
   * carry the QR — `storedVisual = drawingVisual` is a reference assignment, not
   * a snapshot. The evident intent was a QR-free archive, so that is available
   * behind `archiveWithoutQr`; it defaults to today's behaviour because it
   * changes what customers receive.
   */
  archive: Blob
  /** Slot indices left empty because no photo matched. Surface these in the preview. */
  emptySlots: number[]
  /** Photos with no slot to go in (more photos than slots). */
  droppedImages: number[]
  width: number
  height: number
}

export interface CompositeOptions {
  /** Render the archive copy before the QR is drawn. Default false = parity. */
  archiveWithoutQr?: boolean
}

/** Centred square crop: `cropSize = min(w,h)`, offset by half the remainder. */
export function centredSquare(width: number, height: number) {
  const size = Math.min(width, height)
  return {
    sx: Math.trunc((width - size) / 2),
    sy: Math.trunc((height - size) / 2),
    size,
  }
}

export async function composite(
  input: CompositeInput,
  options: CompositeOptions = {},
): Promise<CompositeResult> {
  const { layout, background, images, qr } = input
  const width = background.width
  const height = background.height

  const canvas = makeCanvas(width, height)
  const ctx = canvasContext(canvas, { highQuality: true })

  ctx.drawImage(background, 0, 0, width, height)

  const emptySlots: number[] = []
  const droppedImages: number[] = []

  for (let i = 0; i < images.length; i++) {
    const slot = layout.slots[i]
    if (!slot) {
      droppedImages.push(i)
      continue
    }
    const image = images[i]!

    if (slot.orientation !== image.orientation) {
      emptySlots.push(i)
      continue
    }

    if (slot.ratio === '1:1') {
      // Centre-crop to a square first, then let the destination rect do the
      // resize. The original cropped and resized into an intermediate bitmap;
      // a single drawImage with both rects is the same result in one step.
      const { sx, sy, size } = centredSquare(image.width, image.height)
      ctx.drawImage(image.bitmap, sx, sy, size, size, slot.x, slot.y, slot.width, slot.height)
    } else {
      // 2:3 slots get no crop and no aspect fit — a straight stretch into the
      // slot rect, matching WPF DrawImage(ImageSource, Rect).
      ctx.drawImage(image.bitmap, slot.x, slot.y, slot.width, slot.height)
    }
  }

  // Slots past the photo count are left as bare background, same as today.
  for (let i = images.length; i < layout.slots.length; i++) emptySlots.push(i)

  // The overlay goes on AFTER every photo and BEFORE the QR. Over the photos is
  // the whole point — a frame that only ever sat behind them is the background.
  // Before the QR because the QR has to stay scannable: an overlay drawn last
  // could tint or cover it, and nobody would find out until a guest tried.
  if (input.overlay) ctx.drawImage(input.overlay, 0, 0, width, height)

  // Snapshot the QR-free sheet before the QR goes on, if asked.
  const archiveBlob = options.archiveWithoutQr ? await toBlob(canvas) : null

  if (qr && layout.qr) {
    ctx.drawImage(qr, layout.qr.x, layout.qr.y, layout.qr.width, layout.qr.height)
  }

  const print = await toBlob(canvas)

  return {
    print,
    archive: archiveBlob ?? print,
    emptySlots,
    droppedImages,
    width,
    height,
  }
}
